import { type FirebaseApp, initializeApp } from "firebase/app";
import { getMessaging, onMessage } from "firebase/messaging";
import { nextTick } from "vue";



import { Factory } from "@/data/dao/Factory";
import { CustomerDao } from "@/data/dao/service/CustomerDao";
import { NotificationDao } from "@/data/dao/service/NotificationDao";
import type { ICustomerCart, ICustomerFulfillment } from "@/data/model/Customer";
import { BillType, PaymentFor, ServiceType } from "@/data/model/Enums";
import type { IBillChangedEvent, IBillChangedSharedEvent, IBillDiscountChangedEvent, IBillDiscountChangedSharedEvent, IBillOpenedEvent } from "@/data/model/Events";
import type { IService } from "@/data/model/Service";
import { CurrentAppService } from "@/data/services/CurrentAppService";
import { CurrentIdentityService } from "@/data/services/CurrentIdentityService";
import { CurrentLocationService } from "@/data/services/CurrentLocationService";
import { firConfig, isTestProject } from "@/data/util/Config";
import { Format } from "@/data/util/Format";
import { Misc } from "@/data/util/Misc";
import { OrderHelper } from "@/data/util/OrderHelper";



import { clientLanguage } from "@/shared/localization/language";
import { logError } from "@/shared/logging/errors";
import { logInformation, logWarning } from "@/shared/logging/informations";
import { WindowFocusMonitor } from "@/shared/services/WindowFocusMonitor";
import { Image } from "@/shared/ui/Image";



import { ConnectionState } from "@/components/websocket/ConnectionState";



import { AppContext } from "@/app/context/AppContext";
import { Identity } from "@/app/context/Identity";
import { PayContext } from "@/app/context/PayContext";
import { ViewTransition } from "@/app/controller/BaseController";
import { PendingFulfillmentButton } from "@/app/elements/PendingFulfillmentButton";
import { appEvents as globalEvents } from "@/app/events/appEvents";
import { globalStores } from "@/app/globalStores";
import { trans } from "@/app/localization/Trans";
import { BasePopover } from "@/app/popover/BasePopover";
import { FulfillmentPopover } from "@/app/popover/FulfillmentPopover";
import { AppCurrentAppService } from "@/app/services/AppCurrentAppService";
import { AppCurrentIdentityService } from "@/app/services/AppCurrentIdentityService";
import { AppCurrentLocationService } from "@/app/services/AppCurrentLocationService";
import { ApplicationModeService, setApplicationMode } from "@/app/services/ApplicationModeService";
import { KioskInactivityMonitor } from "@/app/services/KioskInactivityMonitor";
import { colors } from "@/app/services/colors";
import { windowHistoryService } from "@/app/services/windowHistoryService";
import { AddToHomeScreenPopup } from "@/app/ui/AddToHomeScreenPopup";
import { FulfillmentPushNotificationPopup, FulfillmentPushNotificationPopupAction } from "@/app/ui/FulfillmentPushNotificationPopup";
import { Helper } from "@/app/ui/Helper";
import { Interactor } from "@/app/ui/Interactor";
import { NotificationPermissionPopup } from "@/app/ui/NotificattionPermissionPopup";
import { Popup } from "@/app/ui/Popup";
import { Preloader } from "@/app/ui/Preloader";
import { FirebaseHelper } from "@/app/util/FirebaseHelper";
import { PushNotificationsHelper } from "@/app/util/PushNotificationsHelper";



import { ControllerManager } from "./services/ControllerManager";


export type TastierClientConfig = {
    permalinkCode?: string;
    transactionId?: string;
    fulfillmentId?: string;
    pushNotificationAction?: string;
    externalPaymentId?: string;
    mode?: "kiosk" | "normal";
};

export class TastierClient {
    private interactor!: Interactor;
    private windowFocusMonitor!: WindowFocusMonitor;
    public controllerManager!: ControllerManager;
    private kioskInactivityMonitor: KioskInactivityMonitor | undefined = undefined;

    private customerDao = Factory.getInstance<CustomerDao>(CustomerDao);
    private notificationDao = Factory.getInstance<NotificationDao>(NotificationDao);

    public pendingFulfillmentButton?: PendingFulfillmentButton;

    private $mainContainer!: JQuery;

    private preloader!: Preloader;

    //shared objects
    public static identityContext: Identity;
    public static payContext: PayContext;
    public static context: AppContext;
    public static firebaseApp?: FirebaseApp;
    public static isInitialized: boolean = false;

    constructor($mc: JQuery, config: TastierClientConfig) {
        this.$mainContainer = $mc;

        if (isTestProject) {
            return;
        }

        // a Wallee payment returns to the kiosk with the transaction id in the query and init
        // ends by opening the Done popover, so the screen saver must not come up over it
        setApplicationMode(config.mode, config.transactionId == null);


        CurrentLocationService.init(new AppCurrentLocationService());
        CurrentIdentityService.init(new AppCurrentIdentityService());
        CurrentAppService.init(new AppCurrentAppService());

        this.controllerManager = new ControllerManager(this.$mainContainer);
        this.controllerManager.onServiceClicked = (newService) => this.onServiceClicked(newService);

        // only ever acts in kiosk mode, it checks that itself before showing anything
        if (globalStores.kioskStore?.isKioskMode) {
            this.kioskInactivityMonitor = new KioskInactivityMonitor({
                isOrderFlowVisible: () => this.controllerManager.isOrderFlowVisible(),
                isStartPageVisible: () => this.controllerManager.isStartPageVisible(),
            });
            this.kioskInactivityMonitor.enable();
        }


        this.interactor = new Interactor();
        this.preloader = new Preloader(this.$mainContainer.find("> .preloader"));

        if (TastierClient.identityContext === undefined) TastierClient.identityContext = new Identity();

        if (TastierClient.context === undefined) TastierClient.context = new AppContext();

        globalEvents.views.ShowRoomPlan.add((event) => {
            this.controllerManager.showRoomPlan(undefined, event);
        });

        globalEvents.appContext.AllocationChanged.add(() => {
            this.updatePageTitle();
        });
        this.updatePageTitle();

        Identity.onIdentityChanged.add(() => {
            logInformation("On identity changed event has been triggered");
            TastierClient.context.fulfillmentsLoaded = false;
            TastierClient.context.loadFulfillmentsIfNeeded();
        });

        TastierClient.context.onAllocationChange.add((alloc) => {
            TastierClient.resetPayContext();

            if (this.controllerManager.orderController !== undefined) this.controllerManager.orderController.reset();

            if (alloc) {
                this.initOrderContextIfNeeded();
                if (!this.controllerManager.isDoneControllerVisible()) {
                    this.controllerManager.goToFirstViewController(ViewTransition.Fade);
                }
            } else {
                if (this.pendingFulfillmentButton !== undefined) {
                    this.pendingFulfillmentButton.destroy();
                    this.pendingFulfillmentButton = undefined;
                }

                //Hide all popovers
                BasePopover.hideVisiblePopovers();

                this.controllerManager.showAllocation(ViewTransition.Fade);
            }

            // a kiosk terminal stays at its location for good, so the design outlives a
            // deallocation. the session reset between two customers goes through one, and
            // repainting the default colours there flashed the whole chrome green and back
            if (alloc != null || globalStores.kioskStore?.isKioskMode !== true) {
                colors.setUiColor(alloc?.locationDesign?.colorPrimary, alloc?.locationDesign?.color);
            }
        }, this);

        this.interactor.onReactivate = async () => {
            await TastierClient.context.reactivate();
        };

        this.interactor.onCameBackAfterLongTime = () => {
            location.reload();
        };

        document.body.addEventListener(
            "touchmove",
            (e) => {
                const target = $(e.target as any);
                if (target.parents(".scrollable").length === 0 && target.closest(".scrollable-element").length === 0) {
                    e.preventDefault();
                    return;
                }

                if (target.parents(".dragable").length !== 0) {
                    e.preventDefault();
                    return;
                }
            },
            { passive: false }
        );

        this.windowFocusMonitor = new WindowFocusMonitor();

        this.initializeFirebase();
        this.init(config);
    }

    public async handlePermalink(permalinkCode: string) {
        this.controllerManager.allocationController?.setDisabled(true);

        try {
            TastierClient.context.socket.disable();
            await TastierClient.context.allocateByPermalink(permalinkCode);
        } catch (ex) {
            logError(ex);
            Popup.show(trans.allocation.allocationFailed, undefined, undefined, undefined, "allocFailed");
        } finally {
            TastierClient.context.socket.enable();
            this.controllerManager.allocationController?.setDisabled(false);
        }
    }

    // the payment return has to lose its query, or a reload confirms the payment a second time.
    // on a kiosk the address bar goes back to the terminal's own url instead of the bare "/" the
    // app uses, so a restart still finds the location - see AppContext.allocateByPermalink
    private urlWithoutHandledQuery() {
        if (globalStores.kioskStore?.isKioskMode !== true) return "/";

        const code = ApplicationModeService.getInstance().getKioskPermalinkCode();
        return code == null ? "/" : windowHistoryService.kioskUrl(code);
    }

    public async handlePaymentConfirmation(transactionId: string) {
        if (TastierClient.context.currentAllocation === undefined) return;

        windowHistoryService.pushState({ transactionId: transactionId }, this.urlWithoutHandledQuery());

        let wasBillPayment = false;

        try {
            const result = await this.customerDao.checkOrderPaymentConfirmation(transactionId);
            TastierClient.context.updateSelectedService(result.orderServiceId);
            wasBillPayment = result.paymentFor === PaymentFor.Bill;
        } finally {
            this.controllerManager.showDone(
                {
                    transactionId: transactionId,
                },
                undefined,
                () => {
                    this.setupNotifications(false, wasBillPayment);
                }
            );
        }
    }
    public async handlePaymentConfirmationByExternalPaymentId(externalPaymentId: string) {
        if (TastierClient.context.currentAllocation === undefined) {
            return;
        }

        windowHistoryService.pushState({ externalPaymentId: externalPaymentId }, this.urlWithoutHandledQuery());

        let wasBillPayment = false;
        try {
            const result = await this.customerDao.checkOrderPaymentConfirmationByExternalPaymentId(externalPaymentId);
            TastierClient.context.updateSelectedService(result.orderServiceId);
            wasBillPayment = result.paymentFor === PaymentFor.Bill;
        } finally {
            this.controllerManager.showDone(
                {
                    externalPaymentId: externalPaymentId,
                },
                undefined,
                () => {
                    this.setupNotifications(false, wasBillPayment);
                }
            );
        }
    }

    public async handleFulfillmentReady(fulfillmentId: string, pushNotificationAction?: string) {
        try {
            TastierClient.context.socket.disable();

            const fulfillments = await this.customerDao.getActiveFulfillments();
            const fulfillment = fulfillments.find((f) => f.id.toString() === fulfillmentId);

            if (fulfillment == null) {
                logWarning(`HandleFulfillmentReady: No active fulfillment with id ${fulfillmentId} found!`);
                return;
            }

            if (TastierClient.context.currentAllocation === undefined) {
                logInformation(
                    `HandleFulfillmentReady: User ${TastierClient.identityContext.getIdentityId()} not allocated. Allocating to table for order id ${fulfillment.orderId}!`
                );
                await TastierClient.context.allocateByOrderId(fulfillment.orderId);
            }

            if (TastierClient.context.currentAllocation !== undefined) {
                let action = FulfillmentPushNotificationPopupAction.FulfillmentReady;
                if (pushNotificationAction === "OrderReminder") {
                    action = FulfillmentPushNotificationPopupAction.FulfillmentScheduled;
                }

                this.showFulfillmentReadyPopup(fulfillment, action);
            } else {
                logWarning(
                    `HandleFulfillmentReady: Skip showing fulfillment ready popup for fulfillment id ${fulfillmentId}. User not allocated!`
                );
            }

            windowHistoryService.pushState({ fulfillmentId: fulfillmentId }, "/");
        } catch (ex) {
            logError(ex);
        } finally {
            TastierClient.context.socket.enable();
        }
    }

    private showFulfillmentReadyPopup(
        fulfillment: ICustomerFulfillment,
        action: FulfillmentPushNotificationPopupAction
    ) {
        const popoverId = "fulfillment_" + fulfillment.orderNumber;
        FulfillmentPushNotificationPopup.show(
            action,
            () => {
                if (BasePopover.hasCurrentlyVisiblePopover(popoverId)) {
                    return;
                }

                new FulfillmentPopover(
                    {
                        locationName: fulfillment.locationName,
                        orderId: fulfillment.orderId,
                        orderNumber:
                            fulfillment.dailyOrderIncrement !== undefined
                                ? OrderHelper.padDailyIncrement(fulfillment.dailyOrderIncrement)
                                : fulfillment.orderNumber,
                        orderTime: fulfillment.orderDate,
                        tableName: fulfillment.tableName,
                        orderServiceId: fulfillment.orderServiceId,
                        orderScheduledFulfillmentDateTime: fulfillment.orderScheduledFulfillmentDateTime,
                        preorder: fulfillment.preorder,
                        deliveryAddress: fulfillment.deliveryAddress
                    },
                    popoverId
                ).show();

                // Try to get a permission for receiving notifications again due to some strange issue on ios
                const isSafariAndSupportPushNotification = Misc.isSafariAndSupportPushNotifications();
                if (isSafariAndSupportPushNotification) {
                    this.initNotificationsIfGranted();
                }
            },
            {
                fulfillmentId: fulfillment.id,
                locationName: fulfillment.locationName,
                orderId: fulfillment.orderId,
                orderNumber:
                    fulfillment.dailyOrderIncrement !== undefined
                        ? OrderHelper.padDailyIncrement(fulfillment.dailyOrderIncrement)
                        : fulfillment.orderNumber,
                orderTime: fulfillment.orderDate,
                tableName: fulfillment.tableName,
            }
        );
    }

    private initializeFirebase() {
        if (TastierClient.firebaseApp == null) {
            TastierClient.firebaseApp = initializeApp(firConfig);
        }
    }

    public async onServiceClicked(newService: IService) {
        // if it's not order/offer service, then just switch service
        if (newService.serviceType !== ServiceType.Order && newService.serviceType !== ServiceType.Offer) {
            this.updateService(newService);
            return;
        }

        // verify if user has cart from another service
        try {
            const currentCart = await this.customerDao.getCart();
            if (this.hasExistingCartForAnotherOrderService(newService, currentCart)) {
                const cartService = Helper.getOrderOrOfferServiceById(currentCart!.serviceId!);
                if (cartService) {
                    this.showClearCartPopup(newService, cartService!);
                    return;
                }
            }
        } catch (ex) {
            logError(ex);
        }

        // in all the other cases - just switch the service
        this.updateService(newService);
    }

    public hasExistingCartForAnotherOrderService(selectedService: IService, customerCart: ICustomerCart) {
        return customerCart != null && customerCart.serviceId !== selectedService.id && customerCart.items.length > 0;
    }

    public showClearCartPopup(newService: IService, oldService: IService) {
        const lang = clientLanguage();
        let serviceName: string = Format.translate(oldService.name, lang);
        if (serviceName === undefined) {
            serviceName = trans.enums.serviceType[ServiceType[oldService.serviceType]];
        }

        Popup.show(
            trans.cart.haveItemsInCart.replace("{0}", "<b>" + serviceName + "</b>"),
            async () => {
                // Currently we allow to have only one cart per location
                await TastierClient.context.removeCartsFromLocation();
                await this.updateService(newService);
            },
            trans.general.continue,
            trans.general.cancel,
            "items-in-cart",
            false,
            false,
            true
        );
    }

    public async updateService(service: IService) {
        TastierClient.context.updateSelectedService(service.id);
        this.initOrderContextIfNeeded();
        let url: string;

        switch (service.serviceType) {
            case ServiceType.Documents:
                this.controllerManager.showDocuments(ViewTransition.Fade);
                break;
            case ServiceType.Covid:
                this.controllerManager.showCovid(ViewTransition.Fade);
                break;
            case ServiceType.Order:
            case ServiceType.Offer:
                TastierClient.resetPayContext();
                await TastierClient.context.reloadOfferWithAttributes();
                this.controllerManager.showRequiredOrderService(service, ViewTransition.Fade);
                break;
            case ServiceType.Website:
                url = (service.config as any).website;
                if (url !== undefined) {
                    window.open(Format.absoluteUrl(url), "_blank");
                }
                break;
            case ServiceType.Pay:
                TastierClient.resetPayContext();
                await TastierClient.context.reloadBill();
                this.controllerManager.showPayOrder(ViewTransition.Fade);
                await TastierClient.context.selectAllOnEnteringPayService();
                break;
            case ServiceType.RoomPlan:
                this.controllerManager.showRoomPlan(ViewTransition.Fade);
                break;
        }
    }

    private async init(config: TastierClientConfig) {
        ConnectionState.disabled = true;

        const identityInitTask = TastierClient.identityContext.init(config);
        this.preloader.addHandler(Misc.isPrivateMode());
        this.preloader.addHandler(identityInitTask, trans.preloadingSteps.identity);
        await identityInitTask;

        const contextPromise = TastierClient.context.init(config);
        this.preloader.addHandler(contextPromise);
        await contextPromise;

        this.initOrderContextIfNeeded();

        const domAttachmentPromise = new Promise<void>((resolve) => {
            setTimeout(async () => {
                this.controllerManager.initializeBasicControllers();
                await nextTick();
                resolve();
            }, 1);
        });
        this.preloader.addHandler(domAttachmentPromise, trans.preloadingSteps.domStructure);

        this.preloader.addHandler(
            Image.preloadImages([
                "/assets/img/app/loading_green.svg",
                "/assets/img/app/loading_white.svg",
                "/assets/img/app/loadingball_green.svg",
            ]),
            trans.preloadingSteps.assets
        );

        await this.preloader.load();

        ConnectionState.disabled = false;

        if (TastierClient.context.currentAllocation) {
            colors.setUiColor(
                TastierClient.context.currentAllocation.locationDesign.colorPrimary,
                TastierClient.context.currentAllocation.locationDesign.color
            );
            this.controllerManager.goToFirstViewController();
        } else {
            colors.setUiColor(undefined, undefined);
            this.controllerManager.showAllocation();
        }

        this.windowFocusMonitor.focusBackToWindow.add(this.onFocusBackToWindow, this);

        try {
            // setup initial action
            if (config == null) return;

            // a kiosk terminal that lost its session comes up with no allocation, and by then
            // its url can be a bare "/" - the stored code is all that is left to find the
            // location with. it runs before the actions below because they all need one
            if (config.permalinkCode == null && config.mode === "kiosk") {
                const storedKioskCode = this.getKioskCodeToRestore();
                if (storedKioskCode != null) {
                    await this.handlePermalink(storedKioskCode);
                }
            } else {
                if (config.permalinkCode != null) {
                    await this.handlePermalink(config.permalinkCode);
                } else if (config.transactionId != null) {
                    await this.handlePaymentConfirmation(config.transactionId);
                } else if (config.externalPaymentId != null) {
                    await this.handlePaymentConfirmationByExternalPaymentId(config.externalPaymentId);
                } else if (config.fulfillmentId != null) {
                    await this.handleFulfillmentReady(config.fulfillmentId, config.pushNotificationAction);
                }
            }



            // initialize notifications
            const isSafariAndSupportPushNotification = Misc.isSafariAndSupportPushNotifications();
            if (isSafariAndSupportPushNotification) {
                const showNotificationRequestPopup =
                    config.fulfillmentId == null && config.pushNotificationAction == null; //Something is wrong if the app is opened via notification, for some reason, permission to receive notifications cannot be granted
                await this.setupNotifications(true, false, false, false, false, showNotificationRequestPopup);
            } else {
                await this.initNotificationsIfGranted(true);
            }
        } finally {
            TastierClient.isInitialized = true;
            globalEvents.tastierClient.initialized.dispatch();
        }
    }

    private getKioskCodeToRestore() {
        if (globalStores.kioskStore?.isKioskMode !== true) return undefined;
        // the session outlived the reload and the server still knows the location
        if (TastierClient.context.currentAllocation != null) return undefined;

        return ApplicationModeService.getInstance().getKioskPermalinkCode();
    }

    private async initNotificationsIfGranted(ignoreError: boolean = false) {
        TastierClient.context.firebaseMessagingReady = false;
        if (PushNotificationsHelper.IsNotificationAvailable() && PushNotificationsHelper.IsNotificationGranted()) {
            const notificationTokenSet = await this.setNotificationToken(false, !ignoreError);
            if (notificationTokenSet) {
                logInformation("Notification token has been set");
            }
        } else {
            logWarning("Notifications are not available or enabled");
        }
    }

    private async setupNotifications(
        force: boolean = false,
        wasBillPayment: boolean = false,
        showNotificationDeniedPopup: boolean = true,
        showNotificationNotSupportedPopup: boolean = true,
        showAddToHomeScreenPopup: boolean = true,
        showPopupNotificationPermissionRequest: boolean = true
    ) {
        TastierClient.context.firebaseMessagingReady = false;

        if (globalStores.kioskStore?.isKioskMode === true)
        {
            console.log("Kiosk mode is enabled. Do not setup notifications");
            return;
        }

        const useCase = Helper.getCurrentOrderServiceOrderApplication();
        if (useCase == null && !force) {
            logWarning("[setup push Notifications]: No order application found.");
        } else {
            if (PushNotificationsHelper.isPushNotificationsEnabled(useCase) || force === true) {
                if (Misc.privateSurfing === true) {
                    if (wasBillPayment === false) {
                        Popup.show(
                            trans.notification.enableNotificationFailed + "\n" + trans.notification.checkIfPrivateMode
                        );
                    }
                    return;
                }

                if (PushNotificationsHelper.IsNotificationAvailable()) {
                    if (PushNotificationsHelper.IsNotificationGranted()) {
                        await this.setNotificationToken(false, wasBillPayment === false);
                    } else {
                        if (wasBillPayment === false) {
                            if (Notification.permission !== "denied") {
                                if (showPopupNotificationPermissionRequest) {
                                    this.showPopupNotificationPermissionRequest();
                                } else {
                                    logInformation("Do not show notification permission request popup");
                                }
                            } else {
                                if (showNotificationDeniedPopup) {
                                    Popup.show(
                                        trans.notification.notificationsDenied,
                                        undefined,
                                        undefined,
                                        undefined,
                                        "notifications-denied"
                                    );
                                }
                            }
                        }
                    }
                } else {
                    const isSafariAndSupportPushNotifications = Misc.isSafariAndSupportPushNotifications();
                    if (isSafariAndSupportPushNotifications) {
                        if (showAddToHomeScreenPopup) {
                            Popup.show(
                                trans.notification.notificationPermission,
                                () => {
                                    AddToHomeScreenPopup.show(undefined, "notifications-not-enabled-safari");
                                },
                                trans.notification.allow,
                                trans.notification.dontAllow,
                                "notifications-permission",
                                false,
                                true,
                                true
                            );
                        }
                    } else {
                        if (showNotificationNotSupportedPopup) {
                            Popup.show(
                                trans.notification.browserNotSupportNotifications,
                                undefined,
                                undefined,
                                undefined,
                                "notifications-not-enabled"
                            );
                        }
                    }
                }
            } else {
                logWarning("Disabled notifications by use case");
            }
        }

        return;
    }

    private clearFirDatabase(): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            try {
                if (window.indexedDB) {
                    const deleteDBRequest = window.indexedDB.deleteDatabase("firebase-messaging-database");
                    deleteDBRequest.onerror = (event) => {
                        logError("Error occured while deleting FIR database.");
                        logError(event);
                        resolve(false);
                    };

                    deleteDBRequest.onsuccess = (event) => {
                        console.log("FIR database deleted successfully " + event);
                        resolve(true);
                    };
                } else {
                    console.log("indexedDB not available");
                    resolve(false);
                }
            } catch (ex) {
                logError(ex);
                resolve(false);
            }
        });
    }

    private showPopupNotificationPermissionRequest() {
        NotificationPermissionPopup.show(
            trans.notification.notificationPermission,
            () => {
                if (Notification.permission === "granted") {
                    this.clearFirDatabase().finally(() => {
                        this.setNotificationToken(true);
                    });
                }

                if (Notification.permission === "denied") {
                    Popup.show(
                        trans.notification.notificationsDenied,
                        undefined,
                        undefined,
                        undefined,
                        "notifications-denied"
                    );
                }
            },
            () => {
                Popup.show(
                    trans.notification.notificationsDenied,
                    undefined,
                    undefined,
                    undefined,
                    "notifications-denied"
                );
            },
            trans.notification.allow,
            trans.notification.dontAllow,
            undefined,
            false,
            true,
            true
        );
    }

    private async setNotificationToken(
        refreshFulfillmentPopover: boolean = false,
        showErrorPopup: boolean = true
    ): Promise<boolean> {
        let errorOccured = false;
        try {
            const token = await FirebaseHelper.getToken();
            await this.notificationDao.setPushNotificationToken({
                identityId: TastierClient.identityContext.getIdentityId()!,
                deviceHash: TastierClient.identityContext.getDeviceHash()!,
                token: token,
            });

            this.setMessageListenerIfNeeded();

            if (this.controllerManager.doneController != null && this.controllerManager.doneController.isVisible()) {
                this.controllerManager.doneController.refreshPopoverUseCaseSection();
            } else {
                const isIosDevice = Misc.isIosDevice();
                if (
                    refreshFulfillmentPopover === true &&
                    isIosDevice === true &&
                    this.pendingFulfillmentButton != null
                ) {
                    const fulfillment = this.pendingFulfillmentButton.getCurrentFulfillment();

                    if (fulfillment != null) {
                        const popover = new FulfillmentPopover({
                            locationName: fulfillment.locationName,
                            orderId: fulfillment.orderId,
                            orderNumber:
                                fulfillment.dailyOrderIncrement !== undefined
                                    ? OrderHelper.padDailyIncrement(fulfillment.dailyOrderIncrement)
                                    : fulfillment.orderNumber,
                            orderTime: fulfillment.orderDate,
                            tableName: fulfillment.tableName,
                            orderServiceId: fulfillment.orderServiceId,
                            preorder: fulfillment.preorder,
                            orderScheduledFulfillmentDateTime: fulfillment.orderScheduledFulfillmentDateTime,
                            deliveryAddress: fulfillment.deliveryAddress,
                        });
                        popover.show();
                    }
                }
            }
        } catch (ex) {
            errorOccured = true;
            logError(ex);
            if (showErrorPopup) Popup.show(trans.notification.enableNotificationFailed);
        }

        return !errorOccured;
    }

    private setMessageListenerIfNeeded() {
        const messaging = getMessaging(TastierClient.firebaseApp);
        onMessage(messaging, async (payload) => {
            const action = payload.data?.Action;
            if (action != null && (action === "PickupFulfillment" || action === "OrderReminder")) {
                const fulfillmentId = payload.data?.FulfillmentId;

                try {
                    const fulfillments = await this.customerDao.getActiveFulfillments();
                    const fulfillment = fulfillments.find((f) => f.id.toString() === fulfillmentId);
                    if (fulfillment != null) {
                        let fAction = FulfillmentPushNotificationPopupAction.FulfillmentReady;
                        if (action === "OrderReminder") {
                            fAction = FulfillmentPushNotificationPopupAction.FulfillmentScheduled;
                        }

                        this.showFulfillmentReadyPopup(fulfillment, fAction);
                        navigator.vibrate(600);
                    }
                } catch (ex) {
                    logError(ex);
                }
            } else {
                Popup.show(payload.notification?.body ?? "Unknown message");
            }
        });

        TastierClient.context.firebaseMessagingReady = true;
    }

    private initOrderContextIfNeeded() {
        if (Helper.hasService(ServiceType.Order) || Helper.hasService(ServiceType.Pay)) {
            if (TastierClient.payContext === undefined) {
                TastierClient.payContext = new PayContext();
                TastierClient.payContext.onPaymentAccepted = (trxId) => {
                    TastierClient.context.fulfillmentsLoaded = false;
                    if (
                        this.controllerManager.doneController === undefined ||
                        !this.controllerManager.doneController.isVisible()
                    )
                        this.controllerManager.showDone({ transactionId: trxId });

                    TastierClient.payContext.reset();
                };

                TastierClient.payContext.onCartEmptyGoback = () => {
                    this.controllerManager.showOrder({
                        isMainNavigation: false,
                    });
                };

                TastierClient.payContext.onGoBackToFulfillmentTimeView = () => {
                    this.controllerManager.showPreorder({ isMainNavigation: true }, ViewTransition.SlideRight);
                };

                TastierClient.context.socket.onPaymentConfirmed = (co) => {
                    //if its a failing stripe event, ignore since its handlet in app
                    // if (
                    //     co.isConfirmed === false &&
                    //     co.paymentMethodSplitInformation.some((pms) => pms.provider === PaymentProvider.Stripe)
                    // )
                    //     return;

                    TastierClient.context.fulfillmentsLoaded = false;
                    if (
                        this.controllerManager.doneController === undefined ||
                        !this.controllerManager.doneController.isVisible()
                    ) {
                        this.controllerManager.showDone({ confirmation: co }, undefined, () => {
                            const wasBillPayment = co.paymentFor === PaymentFor.Bill;
                            this.setupNotifications(false, wasBillPayment);
                        });
                    }

                    TastierClient.payContext.reset();
                    TastierClient.context.reloadOfferWithAttributes();
                };

                TastierClient.context.socket.onBillOpened = (ev: IBillOpenedEvent) => {
                    const isTableBillForCurrentTable =
                        ev.bill.type == BillType.TableBill &&
                        ev.tableId === TastierClient.context.currentAllocation?.tableId;

                    if (isTableBillForCurrentTable) {
                        TastierClient.context.setCurrentBill(ev.bill);
                        TastierClient.payContext.renewConsumption();
                    }
                };

                TastierClient.context.socket.onBillChanged = (ev: IBillChangedEvent) => {
                    this.handleBillChangedEvent(ev);
                };

                TastierClient.context.socket.onBillChangedShared = (ev: IBillChangedSharedEvent) => {
                    const isIdentityToIgnore = ev.identitiesToIgnore.includes(
                        TastierClient.identityContext.getIdentityId()!
                    );
                    if (isIdentityToIgnore) return;

                    this.handleBillChangedEvent(ev);
                };

                TastierClient.context.socket.onBillDiscountChanged = (ev: IBillDiscountChangedEvent) => {
                    this.handleBillDiscountChangedEvent(ev);
                };

                TastierClient.context.socket.onBillDiscountChangedShared = (ev: IBillDiscountChangedSharedEvent) => {
                    const isIdentityToIgnore = ev.identitiesToIgnore.includes(
                        TastierClient.identityContext.getIdentityId()!
                    );
                    if (isIdentityToIgnore) return;

                    this.handleBillDiscountChangedEvent(ev);
                };

                TastierClient.context.socket.onBillClosed = async (event) => {
                    const currentBillId = TastierClient.context.currentBill?.id;

                    if (currentBillId === event.billId) {
                        console.log("Reload bill due to onBillClosed");
                        await TastierClient.context.reloadBill();
                        TastierClient.payContext.renewConsumption();
                    }
                };

                TastierClient.context.socket.onIdentityEmailChanged = (event) => {
                    TastierClient.identityContext.updateIdentityEmail(event.email);
                };

                TastierClient.context.socket.onProfileChanged = async () => {
                    console.log("refresh user profile");
                    await TastierClient.identityContext.refreshUserProfile();
                };

                TastierClient.context.onOfferChanged.add(() => {
                    TastierClient.payContext.renewConsumption();
                }, this);

                TastierClient.context.onCartChanged.add(() => {
                    TastierClient.payContext.renewConsumption();
                }, this);

                TastierClient.context.onCartDiscountChanged.add(() => {
                    TastierClient.payContext.renewConsumption();
                }, this);
            }

            //add pending fulfillment button if not exists
            if (this.pendingFulfillmentButton == null) {
                this.pendingFulfillmentButton = new PendingFulfillmentButton().renderTo(this.$mainContainer);
                this.pendingFulfillmentButton.onClick = (f) => {
                    const popoverId = "fulfillment_" + f.orderNumber;
                    const popover = new FulfillmentPopover(
                        {
                            locationName: f.locationName,
                            orderId: f.orderId,
                            orderNumber:
                                f.dailyOrderIncrement !== undefined
                                    ? OrderHelper.padDailyIncrement(f.dailyOrderIncrement)
                                    : f.orderNumber,
                            orderTime: f.orderDate,
                            tableName: f.tableName,
                            orderServiceId: f.orderServiceId,
                            orderScheduledFulfillmentDateTime: f.orderScheduledFulfillmentDateTime,
                            preorder: f.preorder,
                            deliveryAddress: f.deliveryAddress,
                        },
                        popoverId
                    );
                    popover.setHideIfOtherAppears(true);
                    popover.show();
                };
                this.pendingFulfillmentButton.startListening();
            }

            TastierClient.context.loadFulfillmentsIfNeeded();
        } else {
            //TODO destroy pay context
            if (this.pendingFulfillmentButton !== undefined) {
                this.pendingFulfillmentButton.destroy();
                this.pendingFulfillmentButton = undefined;
            }
        }
    }

    public static resetPayContext() {
        if (TastierClient.payContext !== undefined) {
            const useCase = Helper.getCurrentUseCase();
            TastierClient.payContext.setDefaultTipInPercent(useCase?.defaultTipPercent);
            TastierClient.payContext.reset();
        }
    }

    private updatePageTitle() {
        const locationName = TastierClient.context?.currentAllocation?.locationName;
        const title = locationName ? `${locationName} - ${trans.app.title}` : trans.app.title;
        document.querySelector("title")!.innerHTML = title;
    }

    private async onFocusBackToWindow() {
        try {
            const userSessionIsValid = await TastierClient.identityContext.validateSession();
            if (!userSessionIsValid) {
                location.reload();
            }
        } catch (ex) {
            logError(ex);
        }
    }

    private handleBillDiscountChangedEvent(ev: IBillDiscountChangedEvent) {
        const currentBillId = TastierClient.context.currentBill?.id;
        if (currentBillId === ev.billId) {
            TastierClient.context.setCurrentBill(ev.bill);
            TastierClient.payContext.renewConsumption();
            TastierClient.payContext.onBillDiscountChanged.dispatch();
        }
    }

    private handleBillChangedEvent(ev: IBillChangedEvent) {
        const currentBillId = TastierClient.context.currentBill?.id;
        if (currentBillId === ev.billId) {
            TastierClient.context.setCurrentBill(ev.bill);
            TastierClient.payContext.renewConsumption();
        }
    }
}
